/**
 * File Usage Reference Utility
 * Handles loading and saving file/column usage tracking to FileUsageReference.json
 */

const WORKSPACE_NAME = "Employee Benefits Expense";
const FILE_NAME = "FileUsageReference.json";

export interface FileUsageEntry {
  [key: string]: string;
}

export interface ProcedureUsage {
  [procedureName: string]: FileUsageEntry[];
}

export interface FileUsageReference {
  [workspaceName: string]: ProcedureUsage;
}

/**
 * Load file usage reference from SharePoint
 */
export async function loadFileUsageReference(): Promise<FileUsageReference> {
  try {
    if ((window as any).sharePointAPI?.downloadClientJson) {
      const result = await (window as any).sharePointAPI.downloadClientJson(FILE_NAME);
      if (result?.success && result?.data) {
        return result.data as FileUsageReference;
      }
    }
  } catch (error) {
    console.error("Error loading file usage reference:", error);
  }
  return {};
}

/**
 * Save file usage reference to SharePoint
 */
export async function saveFileUsageReference(data: FileUsageReference): Promise<boolean> {
  try {
    if ((window as any).sharePointAPI?.uploadClientJson) {
      const result = await (window as any).sharePointAPI.uploadClientJson(FILE_NAME, data);
      return result?.success === true;
    }
  } catch (error) {
    console.error("Error saving file usage reference:", error);
  }
  return false;
}

/**
 * Get file usage for a specific procedure
 */
export async function getProcedureFileUsage(procedureName: string): Promise<FileUsageEntry[]> {
  const reference = await loadFileUsageReference();
  return reference[WORKSPACE_NAME]?.[procedureName] || [];
}

/**
 * Save file usage for a specific procedure
 */
export async function saveProcedureFileUsage(
  procedureName: string,
  usageEntries: FileUsageEntry[]
): Promise<boolean> {
  const reference = await loadFileUsageReference();
  
  if (!reference[WORKSPACE_NAME]) {
    reference[WORKSPACE_NAME] = {};
  }
  
  reference[WORKSPACE_NAME][procedureName] = usageEntries;
  
  return await saveFileUsageReference(reference);
}

/**
 * Update file usage entry for a procedure
 * Merges new entries with existing ones, replacing duplicates by key
 */
export async function updateProcedureFileUsage(
  procedureName: string,
  newEntries: FileUsageEntry[]
): Promise<boolean> {
  const existing = await getProcedureFileUsage(procedureName);
  
  // Create a map of existing entries by their first key
  const entryMap = new Map<string, FileUsageEntry>();
  existing.forEach(entry => {
    const firstKey = Object.keys(entry)[0];
    if (firstKey) {
      entryMap.set(firstKey, entry);
    }
  });
  
  // Merge new entries, replacing by first key
  newEntries.forEach(newEntry => {
    const firstKey = Object.keys(newEntry)[0];
    if (firstKey) {
      entryMap.set(firstKey, newEntry);
    }
  });
  
  // Convert back to array
  const mergedEntries = Array.from(entryMap.values());
  
  return await saveProcedureFileUsage(procedureName, mergedEntries);
}

/**
 * Get file usage for a specific procedure in a specific workspace
 */
export async function getProcedureFileUsageForWorkspace(
  workspaceName: string,
  procedureName: string
): Promise<FileUsageEntry[]> {
  const reference = await loadFileUsageReference();
  return reference[workspaceName]?.[procedureName] || [];
}

/**
 * Save file usage for a specific procedure in a specific workspace
 */
export async function saveProcedureFileUsageForWorkspace(
  workspaceName: string,
  procedureName: string,
  usageEntries: FileUsageEntry[]
): Promise<boolean> {
  const reference = await loadFileUsageReference();
  
  if (!reference[workspaceName]) {
    reference[workspaceName] = {};
  }
  
  reference[workspaceName][procedureName] = usageEntries;
  
  return await saveFileUsageReference(reference);
}

/**
 * Update file usage entry for a procedure in a specific workspace
 * Merges new entries with existing ones, replacing duplicates by key
 */
export async function updateProcedureFileUsageForWorkspace(
  workspaceName: string,
  procedureName: string,
  newEntries: FileUsageEntry[]
): Promise<boolean> {
  const existing = await getProcedureFileUsageForWorkspace(workspaceName, procedureName);
  
  // Create a map of existing entries by their first key
  const entryMap = new Map<string, FileUsageEntry>();
  existing.forEach(entry => {
    const firstKey = Object.keys(entry)[0];
    if (firstKey) {
      entryMap.set(firstKey, entry);
    }
  });
  
  // Merge new entries, replacing by first key
  newEntries.forEach(newEntry => {
    const firstKey = Object.keys(newEntry)[0];
    if (firstKey) {
      entryMap.set(firstKey, newEntry);
    }
  });
  
  // Convert back to array
  const mergedEntries = Array.from(entryMap.values());
  
  return await saveProcedureFileUsageForWorkspace(workspaceName, procedureName, mergedEntries);
}

